import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Row,
  Spinner,
  Nav,
  NavItem,
  NavLink,
  Modal,
  ModalBody
} from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchTranslation,
  fetchLawReport,
  fetchLegalRank,
  fetchLawStatus,
  fetchArticleStatus,
  addLegalRank,
  updateLegalRank,
  deleteLegalRank,
  activateDeactivateLegalRank,
  fetchLawReportTableColumnNames,
  fetchInstrumentType,
  updateUserPreference,
  updateJsonStatus
} from "../../../slices/thunks";
import { getAuthUser } from "../../../helpers/utils";
import ColumnSelectionModal from "../../../Components/Common/ColumnSelectionModal/ColumnSelectionModal";
import DeleteModal from "../../../Components/Common/DeleteModal";
import CommonDataTable from "../../../Components/Common/Table/CommonDataTable";
import usePermissions from "../../../Components/Hooks/usePermissions";
import ConfirmationModal from "../../../Components/Common/ConfirmationModal";
import LawDocumentModal from "../../../Components/Common/Modal/LawDocumentModal";
import * as url from "../../../helpers/url_helper";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";


function LawReport() {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = getAuthUser();
  const [show, setShow] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showLawModal, setShowLawModal] = useState(false);
  const [showStatus, setShowStatus] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isAddOrEdit, setIsAddOrEdit] = useState("isAdd");
  const [selectedRow, setSelectedRow] = useState({});
  const [selectedRowKey, setSelectedRowKey] = useState(null)
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleteModal, setDeleteModal] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [userToToggle, setUserToToggle] = useState(null);
  const [toggleLoading, setToggleLoading] = useState(false);
  const [jsonStatus, setJsonStatus] = useState(1);
  const [lastTranslatedField, setLastTranslatedField] = useState(null);

  const {lawReport, loading, error, tableColumnNames, translationLoading } = useSelector(
    (state) => ({
      tableColumnNames: state.LawReport.tableColumnNames,
      loading: state.LawReport.loading,
      error: state.LawReport.error,
      lawReport: state.LawReport.documents,
      translationLoading: state.Module.translationLoading
    })
  );

  const onTranslateClickHandler = (targetField, textToTranslate, formikSetFieldValue, sourceFieldName) => {
    if (!textToTranslate || textToTranslate.trim() === "") {
    toast.error(t("EnterTextToTranslate"));
    return;
    }
    setLastTranslatedField(sourceFieldName); 

    dispatch(fetchTranslation({ LangText: textToTranslate }))
    .unwrap() 
    .then(translatedText => {
        formikSetFieldValue(targetField, translatedText);
    })
    .catch(error => {
        console.error("Translation error:", error);
        toast.error(t("TranslationFailed"));
    })
    .finally(() => {
        setLastTranslatedField(null); 
    });
  };


  const handleClassesClicks = (value) => {
    setShow(true);
    setIsAddOrEdit(value);
    setSelectedRow({});
  };

  const onEditClickHandler = (user, value) => {
    setSelectedRow(user);
    setIsAddOrEdit(value);
    setShow(true);
  };

  const handlePageChange = (page, record) => {
    console.log('record: ', record);
    console.log('page: ', page);
    dispatch(fetchLawReport({ Status: jsonStatus, Limit: record, Page: page }));
    // You can add any additional logic here if needed
  }
    
  const handleTabChange = (status) => {
    setJsonStatus(status);
    dispatch(fetchLawReport({ Status: status }));
  }

  const onSaveClickHandler = async () => {
    if (isSaving) return;
    
    setIsSaving(true);
    try {
      const payload = {
        DocID: selectedRow.ID,
        JsonURL: selectedRow.JsonURL,
        DocumentTypeID: selectedRow.DocumentTypeID,
        Status: selectedRow.Status,
        pushToPinecone: true,
        documents: selectedRow.Json
      }

      const response = await fetch(url.SAVE_DOC_DETAILS, {
        method: "POST",
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload), 
      });

      if (!response.body) throw new Error("No response body for stream");

      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let buffer = "";

      // Process streaming response
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split("\n\n");
        buffer = events.pop(); // keep last incomplete chunk

        for (const eventBlock of events) {
          if (!eventBlock.trim()) continue;

          let eventName = "";
          let eventData = "";

          for (const line of eventBlock.split("\n")) {
            if (line.startsWith("event:"))
              eventName = line.slice(6).trim();
            else if (line.startsWith("data:"))
              eventData += line.slice(5).trim();
          }

          if (!eventName || !eventData) continue;

          try {
            const parsedData = JSON.parse(eventData);
            if (eventName === "error") {
              throw new Error('Something Went Wrong')
            }

            if (eventName === "file_saved") {
              setSelectedRow({});
              setShowLawModal(false);
            }

          } catch (err) {
            toast.error(t('Something Went Wrong'));
            console.warn("Invalid SSE event data:", eventData, err);
          }
        }
      }
    } catch (error) {
      console.error("Save stream error:", error);
    } finally {
      setIsSaving(false); // ✅ only after stream fully ends
    }
  }

  const saveUserPreferences = async () => {
    const preferencesPayload = {
      ColumnIDs: tableColumnNames.reduce((acc, column) => {
        acc[column.ID] = selectedColumns.includes(column.ID);
        return acc;
      }, {}),
    };

    setModalOpen(false);
    try {
      await dispatch(updateUserPreference(preferencesPayload)).unwrap();
      await dispatch(
        fetchLegalRankTableColumnNames({
          module: "LAW_REPORT",
        })
      );

    } catch (error) {
      console.error("Error updating preferences:", error);
    }
  };

  const confirmDelete = () => {
    dispatch(deleteLegalRank({ IDs: selectedIds }))
      .then(() => {
        setSelectedIds([]);
      })
      .catch((error) => {
        console.error("Error deleting:", error);
      });
    setDeleteModal(false);
  };

  const onActiveOrDeactiveChange = (userItem, e) => {
    setUserToToggle(userItem);
    setShowConfirmationModal(true);
  };

  const handleConfirmToggleStatus = async () => {
    if (userToToggle) {
      setToggleLoading(true);
      try {
        // await dispatch(
        //   activateDeactivateLegalRank({
        //     ID: userToToggle.ID,
        //   })
        // ).unwrap(); 
      } catch (error) {
        console.error("Failed to toggle user status:", error);
        // Handle error (e.g., show a toast message)
      } finally {
        setToggleLoading(false);
        setShowConfirmationModal(false);
        setUserToToggle(null);
      }
    }
  };

  const handleViewClick = (item) => {
    window.open(item.FileURL, '_blank');
  }

  const handleStatusChange = (item) => {
    setSelectedRow(item);
    setShowStatus(true);
  }

  const UpdateDocumentStatus = async () => {
    dispatch()(updateJsonStatus({ ID: selectedRow.ID, JsonStatus: 2 }))
    setSelectedRow({});
    setShowStatus(false);
  };

  // Compute permission
  const permission = (() => {
    return usePermissions(1, 21);
  })();

  const actionConfig = {
    view: { 
      icon: "ri-eye-line",
      tooltip: t("View"),
      onClick: handleViewClick,
      isVisible: (item, perm) => true, // Show for all items, or add permission check: perm?.CanView
    },
    save: {
      icon: "ri-save-line",
      tooltip: t("Save"),
      onClick: handleStatusChange,
      isVisible: (item, perm) => true 
    },
    // edit: {
    //   icon: "ri-edit-2-line",
    //   tooltip: "Edit",
    // },
    // delete: {
    //   icon: "ri-delete-bin-2-line",
    //   tooltip: "Delete",
    // },
  };


  const handleRowClick = (item, key) => {
    setSelectedRowKey(key);
    setSelectedRow(item);
    setShowLawModal(true)
  }


  useEffect(() => {
    const fetchData = async () => {
      try {
        await dispatch(
          fetchLawReportTableColumnNames({
            module: "LAW_REPORT",
          })
        ).unwrap();
        dispatch(fetchLawReport({ Status: jsonStatus }));
        dispatch(fetchLegalRank());
        dispatch(fetchLawStatus({ Type: 10}));
        // dispatch(fetchArticleStatus({ Type: 11 }));
        dispatch(fetchInstrumentType({ Type: 12 }));
      } catch (error) {
        console.error(error);
      }
    };

    fetchData();
  }, [dispatch]);

  return (
    <div className="page-content">
      <Container fluid>
        <Row className="mb-3">
          <Col>
            <BreadCrumb title={t("Law Report")} />
          </Col>
        </Row>

        <div className="table-responsive">
          <Card>
            <CardHeader>
                <Row className="g-4 ">
                  <Col className="col-sm-auto">
                    <div>
                      <Button
                        className="btn btn-soft-danger ms-2 me-2"
                        onClick={() => setDeleteModal(true)}
                        disabled={selectedIds.length === 0}
                      >
                        <i className="ri-delete-bin-2-line"></i>
                      </Button>
                      {/* {permission.CanAdd && (
                        <Button
                          color="secondary"
                          className="add-btn me-1"
                          onClick={() => handleClassesClicks("isAdd")}
                          id="create-btn"
                          disabled={loading}
                        >
                          <i className="ri-add-line align-bottom me-1 ms-1"></i>
                          {t("AddNew")}
                        </Button>
                      )} */}
                    </div>
                  </Col>
                  <Col className="col-sm">
                    <div className="d-flex justify-content-sm-end">
                      <Button
                        className="btn btn-soft"
                        onClick={() => setModalOpen(true)}
                        disabled={loading}
                      >
                        <i className="ri-filter-3-line"></i>
                      </Button>
                      <div className="search-box ms-2 me-2">
                        <Input
                          type="text"
                          className="form-control search"
                          placeholder={t("Search")}
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <i className="ri-search-line search-icon"></i>
                      </div>
                    </div>
                  </Col>
                </Row>
            </CardHeader>

            <CardBody>
              <div className="listjs-table" id="customerList">
                <Nav tabs style={{ marginBottom: 16 }}>
                  <NavItem>
                    <NavLink
                      className={jsonStatus == 1 ? "active" : ""}
                      onClick={() => handleTabChange(1)}
                      style={{ cursor: "pointer" }}
                    >
                      {t('New')}
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={jsonStatus == 2 ? "active" : ""}
                      onClick={() => handleTabChange(2)}
                      style={{ cursor: "pointer" }}
                    >
                      {t('Reviewed')}
                    </NavLink>
                  </NavItem>
                </Nav>
             
                {loading ? (
                  <div className="d-flex justify-content-center align-items-center">
                    <Spinner
                      style={{ height: "3rem", width: "3rem" }}
                      className="me-2"
                    >
                      Loading...
                    </Spinner>
                  </div>
                ) : (
                  <div className="listjs-table" id="customerList">
                     <CommonDataTable
                      data={lawReport}
                      tableColumnNames={tableColumnNames}
                      searchTerm={searchTerm}
                      onEditClickHandler={onEditClickHandler}
                      onActiveOrDeactiveChange={onActiveOrDeactiveChange}
                      onRowClick={handleRowClick}
                      setDeleteModal={setDeleteModal}
                      setSelectedIds={setSelectedIds}
                      actionConfig={actionConfig}
                      permission={permission}
                      onPageChange={handlePageChange}
                      totalRecords={1000}
                    />
                  </div>
                )}
              </div>
            </CardBody>
          </Card>
        </div>
      </Container>

      {/* ColumnSelectionModal */}
      <ColumnSelectionModal
        isOpen={modalOpen}
        toggle={() => setModalOpen(!modalOpen)}
        columns={tableColumnNames}
        selectedColumns={selectedColumns}
        setSelectedColumns={setSelectedColumns}
        handleSave={saveUserPreferences}
      />

      {/* Delete modal  */}
      <DeleteModal
        showDelete={deleteModal}
        setShowDelete={setDeleteModal}
        onDeleteClick={confirmDelete}
        setSelectedIds={setSelectedIds}
        // description={
        //   selectedIds.length > 1
        //     ? `Are you sure you want to delete ${selectedIds.length} selected items?`
        //     : `Are you sure you want to delete this record?`
        // }
        description={t("confirmDelete")}
      />

      {/* Confirmation Modal for Activate/Deactivate */}
      <ConfirmationModal
        show={showConfirmationModal}
        setShow={setShowConfirmationModal}
        onConfirmClick={handleConfirmToggleStatus}
        item={userToToggle}
        loading={toggleLoading}
      />

      <LawDocumentModal
        show={showLawModal}
        setShow={setShowLawModal}
        fieldKey={selectedRowKey}
        selectedRow={selectedRow}
        setSelectedRow={setSelectedRow}
        onSaveClickHandler={onSaveClickHandler}
      />

      <Modal isOpen={showStatus} centered={true}>
        <ModalBody className="py-3 px-5">
          <div className="mt-2 text-center">
            <div className="mt-4 pt-2 fs-15 mx-4 mx-sm-5">
              <h4>{t('areYouSure')}</h4>
              <p className="text-muted mx-4 mb-0">{t('Are you sure you want to update the status of this document?')}</p>
            </div>
          </div>
          <div className="d-flex gap-2 justify-content-center mt-4 mb-2">
            <button
              type="button"
              className="btn w-sm btn-light"
              data-bs-dismiss="modal"
              onClick={() => setShowStatus(false)}
            >
              {t("Close")}
            </button>
            <button
              type="button"
              className="btn w-sm btn-danger d-flex align-items-center"
              disabled={isSaving}
              onClick={UpdateDocumentStatus}
            >
              {t('Yes, Update it')}
            </button>
          </div>
        </ModalBody>
      </Modal>
    </div>
  );
}

export default LawReport;
